import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import {
  ClipboardList, Building2, FileWarning, LogOut, ChevronLeft,
  Eye, FileText, Mail, ChevronRight, X, Download
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const LOGO_LIGHT = "https://customer-assets.emergentagent.com/job_444ea87a-9e43-44f5-894f-5ab28ef3d9aa/artifacts/9vynnk18_Captura%20de%20Pantalla%202026-04-22%20a%20la%28s%29%203.31.11%20p.%C2%A0m..png";

// ============ Datos mock para el flujo de evaluación (UI-only) ============
const DOCUMENTOS_BASE = [
  { id: 1, name: 'RUT' },
  { id: 2, name: 'Cámara de Comercio' },
  { id: 3, name: 'Certificación Calidad' },
  { id: 4, name: 'Certificación de Exportación' },
  { id: 5, name: 'Documento Contrapartida' },
  { id: 6, name: 'Anexos' },
];

const SUBSANACIONES_MOCK = [
  { id: 1, companyName: 'Constructora del Norte S.A.', representative: 'Ing. Roberto Sánchez Díaz', documentName: 'Certificado de Antecedentes', oldObservation: 'Falta sello institucional', oldDate: '15/07/2026', newDate: '22/07/2026', status: 'pending' },
  { id: 2, companyName: 'Servicios Técnicos Ltda.', representative: 'Lic. María González Rodríguez', documentName: 'Balance Financiero 2025', oldObservation: 'Fecha ilegible en el documento', oldDate: '18/07/2026', newDate: '23/07/2026', status: 'pending' },
  { id: 3, companyName: 'Innovaciones Digitales S.A.S.', representative: 'Ing. Carlos Ramírez Torres', documentName: 'Certificado Bancario', oldObservation: 'Documento vencido', oldDate: '10/07/2026', newDate: '20/07/2026', status: 'pending' },
];

// Human-friendly labels for known form field keys
const FIELD_LABELS = {
  certificacion_objetivo: 'Certificación Objetivo',
  documento_camara: 'Cámara de Comercio',
  documento_rut: 'RUT',
  rut: 'RUT',
  documento_exportacion: 'Certificado de Exportación',
  contrapartida: 'Documento Contrapartida',
  documento_contrapartida: 'Documento Contrapartida',
  anexo: 'Anexo',
  anexos: 'Anexos',
  balance: 'Balance Financiero',
  certificado_bancario: 'Certificado Bancario',
};

const humanizeFieldName = (key) => {
  if (FIELD_LABELS[key]) return FIELD_LABELS[key];
  if (/^\d+$/.test(key)) return `Documento ${key.slice(-3)}`;
  return key.replace(/[_-]/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
};

const getFileExt = (url) => (url || '').split('.').pop().split('?')[0].toLowerCase();

// ============ Styles helpers ============
const statusBadge = (kind) => {
  const map = {
    active:    { bg: '#dcfce7', color: '#166534', label: 'Activa' },
    pending:   { bg: '#fef3c7', color: '#92400e', label: 'En evaluación' },
    completed: { bg: '#dbeafe', color: '#1e40af', label: 'Aprobado' },
    rejected:  { bg: '#fee2e2', color: '#dc2626', label: 'Rechazado' },
    subsan:    { bg: '#fef3c7', color: '#92400e', label: 'Requiere subsanación' },
  };
  const s = map[kind] || map.pending;
  return (
    <span style={{ background: s.bg, color: s.color, padding: '0.25rem 0.75rem', borderRadius: '12px', fontSize: '0.75rem', fontWeight: 600, whiteSpace: 'nowrap' }}>
      {s.label}
    </span>
  );
};

// ============ Componente principal ============
export default function EvaluadorDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [section, setSection] = useState('menu'); // menu | convocatorias | postulados | evaluacion | subsanaciones | logout
  const [convocatorias, setConvocatorias] = useState([]);
  const [postulaciones, setPostulaciones] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentCompany, setCurrentCompany] = useState(null);
  const [documentsState, setDocumentsState] = useState([]);
  const [subsanaciones, setSubsanaciones] = useState([]);
  const [currentSubsanation, setCurrentSubsanation] = useState(null);
  const [modal, setModal] = useState(null); // 'preview' | 'report' | 'notify' | 'subsanacion' | 'file' | null
  const [previewFile, setPreviewFile] = useState(null); // { name, url }

  // Load subsanaciones from backend
  const loadSubsanaciones = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const { data } = await axios.get(`${API}/evaluaciones/subsanaciones`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const items = (data.subsanaciones || []).map((s) => ({
        id: s.id,
        documento_key: s.documento_key,
        postulacion_id: s.postulacion_id,
        companyName: s.postulante_nombre || 'Empresa',
        representative: s.postulante_email || '—',
        documentName: s.documento_nombre,
        oldObservation: s.observacion || '',
        oldDate: s.updated_at ? new Date(s.updated_at).toLocaleDateString('es-CO') : '',
        newDate: s.fecha_resubmit ? new Date(s.fecha_resubmit).toLocaleDateString('es-CO') : '',
        newUrl: s.documento_url_nuevo,
        oldUrl: s.documento_url_original,
        status: 'pending',
      }));
      setSubsanaciones(items);
    } catch {
      /* silent */
    }
  }, []);

  // ===== fetch data =====
  useEffect(() => {
    const token = localStorage.getItem('token');
    const headers = { Authorization: `Bearer ${token}` };
    const load = async () => {
      setLoading(true);
      try {
        const [convRes, postRes] = await Promise.all([
          axios.get(`${API}/convocatorias`, { headers }).catch(() => ({ data: [] })),
          axios.get(`${API}/postulaciones/todas?limit=200`, { headers }).catch(() => ({ data: { postulaciones: [] } })),
        ]);
        setConvocatorias(convRes.data || []);
        setPostulaciones(postRes.data?.postulaciones || []);
      } catch (e) {
        toast.error('Error al cargar datos');
      } finally {
        setLoading(false);
      }
    };
    load();
    loadSubsanaciones();
  }, [loadSubsanaciones]);

  // Estadísticas
  const stats = useMemo(() => {
    const totalPost = postulaciones.length;
    const evaluadas = postulaciones.filter(p => ['aprobada', 'rechazada'].includes((p.estado || '').toLowerCase())).length;
    const pendientes = totalPost - evaluadas;
    return { totalPost, evaluadas, pendientes };
  }, [postulaciones]);

  const handleLogout = () => {
    logout();
    toast.success('Sesión cerrada');
    navigate('/login');
  };

  const openEvaluar = (postulacion) => {
    setCurrentCompany({
      id: postulacion.id,
      name: postulacion.user_name || postulacion.company_name || 'Sin nombre',
      representative: postulacion.legal_representative || postulacion.user_name || '—',
      convocatoria: postulacion.convocatoria_nombre || postulacion.convocatoria_codigo || '',
    });
    // Extract real file attachments from respuestas_formulario
    const respuestas = postulacion.respuestas_formulario || {};
    const realDocs = Object.entries(respuestas)
      .filter(([_, v]) => typeof v === 'string' && v.startsWith('/api/uploads/'))
      .map(([key, url], idx) => ({
        id: `${postulacion.id}-${idx}`,
        name: humanizeFieldName(key),
        url,
        status: 'pending',
        observation: '',
      }));
    const docs = realDocs.length > 0
      ? realDocs
      : DOCUMENTOS_BASE.map(d => ({ ...d, status: 'pending', observation: '' }));
    setDocumentsState(docs);
    setSection('evaluacion');
  };

  return (
    <div style={{ minHeight: '100vh', background: '#f5f7fa', fontFamily: `-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif` }}>
      {/* Header */}
      <header style={{ background: '#0f172a', padding: '1rem 2rem', display: 'flex', alignItems: 'center', gap: '1rem', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
        <img src={LOGO_LIGHT} alt="iNNpulsa" style={{ height: 44, background: 'white', padding: 4, borderRadius: 4, objectFit: 'contain' }} />
        <div style={{ color: 'white', flex: 1 }}>
          <div style={{ fontSize: '1.25rem', fontWeight: 600 }}>Panel Evaluador</div>
          <div style={{ fontSize: '0.85rem', opacity: 0.75 }}>iNNpulsa Colombia · MinCIT</div>
        </div>
        <div style={{ color: 'white', textAlign: 'right' }} data-testid="evaluador-user-info">
          <div style={{ fontSize: '0.85rem', opacity: 0.75 }}>Evaluador</div>
          <div style={{ fontSize: '0.95rem', fontWeight: 600 }}>{user?.first_name || 'Carlos'} {user?.first_lastname || ''}</div>
        </div>
      </header>

      <div style={{ maxWidth: 1200, margin: '0 auto', padding: '2rem' }}>
        {section === 'menu' && (
          <MenuView onSelect={setSection} onLogout={() => setSection('logout')} userName={user?.first_name || 'Carlos'} />
        )}

        {section === 'convocatorias' && (
          <ConvocatoriasView
            loading={loading}
            convocatorias={convocatorias}
            postulaciones={postulaciones}
            onBack={() => setSection('menu')}
          />
        )}

        {section === 'postulados' && (
          <PostuladosView
            loading={loading}
            postulaciones={postulaciones}
            stats={stats}
            onBack={() => setSection('menu')}
            onEvaluar={openEvaluar}
          />
        )}

        {section === 'evaluacion' && currentCompany && (
          <EvaluacionView
            company={currentCompany}
            documents={documentsState}
            setDocuments={setDocumentsState}
            onBack={() => setSection('postulados')}
            openPreview={() => setModal('preview')}
            openReport={() => setModal('report')}
            openNotify={() => setModal('notify')}
          />
        )}

        {section === 'subsanaciones' && (
          <SubsanacionesView
            subsanaciones={subsanaciones}
            onBack={() => setSection('menu')}
            onRevisar={(s) => { setCurrentSubsanation(s); setModal('subsanacion'); }}
          />
        )}

        {section === 'logout' && (
          <LogoutView onCancel={() => setSection('menu')} onConfirm={handleLogout} />
        )}
      </div>

      {/* Modals */}
      {modal === 'preview' && currentCompany && (
        <PreviewModal
          company={currentCompany}
          documents={documentsState}
          onClose={() => setModal(null)}
          onSave={async (docs) => {
            try {
              const token = localStorage.getItem('token');
              const payload = {
                postulacion_id: currentCompany.id,
                documents: docs
                  .filter(d => d.status === 'approved' || d.status === 'subsanation')
                  .map(d => ({
                    key: d.id,
                    name: d.name,
                    url: d.url,
                    status: d.status,
                    observation: d.observation,
                  })),
              };
              if (payload.documents.length === 0) {
                toast.error('Debes marcar al menos un documento como aprobado o solicitar subsanación');
                return;
              }
              const { data } = await axios.post(`${API}/evaluaciones/batch`, payload, {
                headers: { Authorization: `Bearer ${token}` },
              });
              setDocumentsState(docs);
              toast.success(`${data.saved} decisión(es) guardada(s)`);
              setModal(null);
            } catch (e) {
              toast.error(e.response?.data?.detail || 'Error al guardar las decisiones');
            }
          }}
          onPreviewFile={(f) => setPreviewFile(f)}
        />
      )}
      {previewFile && (
        <FilePreviewModal file={previewFile} onClose={() => setPreviewFile(null)} />
      )}
      {modal === 'report' && (
        <ReportModal
          postulaciones={postulaciones}
          onClose={() => setModal(null)}
          onGenerate={() => { toast.success('Informe generado (mock)'); setModal(null); }}
        />
      )}
      {modal === 'notify' && (
        <NotifyModal
          postulaciones={postulaciones}
          onClose={() => setModal(null)}
          onSend={() => { toast.success('Notificación enviada (mock)'); setModal(null); }}
        />
      )}
      {modal === 'subsanacion' && currentSubsanation && (
        <SubsanationReviewModal
          subsanation={currentSubsanation}
          onPreviewFile={(f) => setPreviewFile(f)}
          onClose={() => setModal(null)}
          onDecide={async (decision) => {
            try {
              const token = localStorage.getItem('token');
              const newStatus = decision === 'approved' ? 'approved' : 'subsanation';
              await axios.post(`${API}/evaluaciones/batch`, {
                postulacion_id: currentSubsanation.postulacion_id,
                documents: [{
                  key: currentSubsanation.documento_key,
                  name: currentSubsanation.documentName,
                  url: currentSubsanation.newUrl,
                  status: newStatus,
                  observation: decision === 'rejected' ? 'Subsanación rechazada — requiere nueva versión' : '',
                }],
              }, { headers: { Authorization: `Bearer ${token}` } });
              toast.success(decision === 'approved' ? 'Subsanación aprobada' : 'Subsanación rechazada');
              loadSubsanaciones();
              setModal(null);
            } catch {
              toast.error('Error al actualizar la subsanación');
            }
          }}
        />
      )}
    </div>
  );
}

// ===================== Subcomponentes =====================

const Card = ({ children, style }) => (
  <div style={{ background: 'white', padding: '2rem', borderRadius: 8, border: '1px solid #e2e8f0', marginBottom: '1rem', ...style }}>
    {children}
  </div>
);

const SectionHeader = ({ title, onBack, testId }) => (
  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.5rem', gap: 12, flexWrap: 'wrap' }}>
    <h2 style={{ fontSize: '1.5rem', fontWeight: 700, color: '#0f172a', margin: 0 }}>{title}</h2>
    <button
      onClick={onBack}
      data-testid={testId}
      style={{ background: '#3b82f6', color: 'white', border: 'none', padding: '0.5rem 1rem', borderRadius: 6, cursor: 'pointer', fontSize: '0.875rem', fontWeight: 500, display: 'inline-flex', alignItems: 'center', gap: 6 }}
    >
      <ChevronLeft size={16} /> Volver al menú
    </button>
  </div>
);

const StatsGrid = ({ stats }) => (
  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem', marginBottom: '1.5rem' }}>
    {stats.map((s) => (
      <div key={s.label} style={{ background: '#f8fafc', padding: '1.5rem', borderRadius: 6, border: '1px solid #e2e8f0', textAlign: 'center' }}>
        <div style={{ fontSize: '2rem', fontWeight: 700, color: '#0f172a', lineHeight: 1 }}>{s.value}</div>
        <div style={{ fontSize: '0.85rem', color: '#64748b', marginTop: 6 }}>{s.label}</div>
      </div>
    ))}
  </div>
);

// ---------- Menu ----------
const MenuView = ({ onSelect, onLogout, userName }) => {
  const items = [
    { id: 'convocatorias', title: 'Convocatorias', desc: 'Ver y gestionar convocatorias disponibles', icon: ClipboardList },
    { id: 'postulados', title: 'Postulados', desc: 'Revisar y evaluar empresas postuladas', icon: Building2 },
    { id: 'subsanaciones', title: 'Subsanaciones', desc: 'Revisar documentos pendientes de corrección', icon: FileWarning },
    { id: 'logout', title: 'Cerrar Sesión', desc: 'Salir del sistema de forma segura', icon: LogOut },
  ];
  return (
    <div data-testid="evaluador-menu">
      <Card style={{ marginBottom: '2rem' }}>
        <h1 style={{ fontSize: '1.875rem', fontWeight: 700, color: '#0f172a', margin: 0 }}>Bienvenido, {userName}</h1>
        <p style={{ color: '#64748b', marginTop: 6, marginBottom: 0 }}>Gestiona las evaluaciones desde tu panel de control</p>
      </Card>
      <div style={{ display: 'grid', gap: '1rem' }}>
        {items.map((it) => {
          const Icon = it.icon;
          return (
            <button
              key={it.id}
              onClick={() => (it.id === 'logout' ? onLogout() : onSelect(it.id))}
              data-testid={`menu-item-${it.id}`}
              style={{
                background: 'white', border: '1px solid #e2e8f0', borderRadius: 8, padding: '1.5rem',
                display: 'flex', alignItems: 'center', gap: '1rem', cursor: 'pointer', textAlign: 'left',
                transition: 'all 0.2s', width: '100%',
              }}
              onMouseEnter={(e) => { e.currentTarget.style.borderColor = '#3b82f6'; e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.05)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.borderColor = '#e2e8f0'; e.currentTarget.style.transform = 'none'; e.currentTarget.style.boxShadow = 'none'; }}
            >
              <div style={{ background: '#f1f5f9', padding: 12, borderRadius: 8, display: 'flex' }}>
                <Icon size={24} color="#475569" />
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: '1.125rem', fontWeight: 600, color: '#0f172a' }}>{it.title}</div>
                <div style={{ fontSize: '0.875rem', color: '#64748b', marginTop: 4 }}>{it.desc}</div>
              </div>
              <ChevronRight size={20} color="#94a3b8" />
            </button>
          );
        })}
      </div>
    </div>
  );
};

// ---------- Convocatorias ----------
const ConvocatoriasView = ({ loading, convocatorias, postulaciones, onBack }) => {
  const activas = convocatorias.filter(c => c.estado === 'Activa').length;
  const totalEmpresas = new Set(postulaciones.map(p => p.user_id)).size;
  return (
    <Card>
      <SectionHeader title="Convocatorias" onBack={onBack} testId="btn-back-convocatorias" />
      <StatsGrid stats={[
        { label: 'Convocatorias Activas', value: activas },
        { label: 'Total Convocatorias', value: convocatorias.length },
        { label: 'Total Empresas Postuladas', value: totalEmpresas },
      ]} />
      {loading && <div style={{ color: '#64748b', padding: '1rem' }}>Cargando…</div>}
      <div style={{ display: 'grid', gap: '0.75rem' }} data-testid="convocatorias-list">
        {convocatorias.length === 0 && !loading && (
          <div style={{ color: '#64748b', padding: '2rem', textAlign: 'center', background: '#f8fafc', borderRadius: 6 }}>No hay convocatorias registradas.</div>
        )}
        {convocatorias.map((c) => {
          const kind = c.estado === 'Activa' ? 'active' : (c.estado === 'Cerrada' ? 'completed' : 'pending');
          const kindLabel = { active: c.estado, completed: c.estado, pending: c.estado }[kind];
          return (
            <div key={c.id || c.codigo} style={{ background: '#f8fafc', padding: '1rem 1.25rem', borderRadius: 6, border: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
              <div style={{ flex: 1, minWidth: 250 }}>
                <div style={{ fontWeight: 600, color: '#0f172a' }}>{c.codigo} — {c.nombre}</div>
                <div style={{ fontSize: '0.85rem', color: '#64748b', marginTop: 4 }}>
                  {c.area || ''} · Fecha límite: {c.fecha_final || '—'}
                </div>
              </div>
              <span style={{ background: kind === 'active' ? '#dcfce7' : kind === 'completed' ? '#dbeafe' : '#fef3c7', color: kind === 'active' ? '#166534' : kind === 'completed' ? '#1e40af' : '#92400e', padding: '0.25rem 0.75rem', borderRadius: 12, fontSize: '0.75rem', fontWeight: 600 }}>
                {kindLabel}
              </span>
            </div>
          );
        })}
      </div>
    </Card>
  );
};

// ---------- Postulados ----------
const PostuladosView = ({ loading, postulaciones, stats, onBack, onEvaluar }) => (
  <Card>
    <SectionHeader title="Empresas Postuladas" onBack={onBack} testId="btn-back-postulados" />
    <StatsGrid stats={[
      { label: 'Total Postulaciones', value: stats.totalPost },
      { label: 'Evaluadas', value: stats.evaluadas },
      { label: 'Pendientes', value: stats.pendientes },
    ]} />
    {loading && <div style={{ color: '#64748b', padding: '1rem' }}>Cargando…</div>}
    <div style={{ display: 'grid', gap: '0.75rem' }} data-testid="postulados-list">
      {postulaciones.length === 0 && !loading && (
        <div style={{ color: '#64748b', padding: '2rem', textAlign: 'center', background: '#f8fafc', borderRadius: 6 }}>No hay postulaciones registradas.</div>
      )}
      {postulaciones.map((p) => (
        <div key={p.id} style={{ background: '#f8fafc', padding: '1rem 1.25rem', borderRadius: 6, border: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
          <div style={{ flex: 1, minWidth: 250 }}>
            <div style={{ fontWeight: 600, color: '#0f172a' }}>{p.user_name || p.company_name || 'Empresa'}</div>
            <div style={{ fontSize: '0.85rem', color: '#64748b', marginTop: 4 }}>
              {p.convocatoria_codigo || p.convocatoria_id} · {p.user_email || ''}
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            {statusBadge((p.estado || '').toLowerCase().includes('aprob') ? 'completed' : (p.estado || '').toLowerCase().includes('rechaz') ? 'rejected' : 'pending')}
            <button
              onClick={() => onEvaluar(p)}
              data-testid={`btn-evaluar-${p.id}`}
              style={{ background: '#3b82f6', color: 'white', border: 'none', padding: '0.5rem 1rem', borderRadius: 6, cursor: 'pointer', fontSize: '0.85rem', fontWeight: 500 }}
            >
              Evaluar
            </button>
          </div>
        </div>
      ))}
    </div>
  </Card>
);

// ---------- Evaluación ----------
const EvaluacionView = ({ company, documents, setDocuments, onBack, openPreview, openReport, openNotify }) => {
  const statusText = (s) => ({ pending: 'Pendiente', approved: 'Aprobado', subsanation: 'Requiere subsanación' }[s] || 'Pendiente');
  return (
    <Card>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.5rem', gap: 12, flexWrap: 'wrap' }}>
        <h2 style={{ fontSize: '1.5rem', fontWeight: 700, color: '#0f172a', margin: 0 }}>Evaluación de Empresa</h2>
        <button onClick={onBack} data-testid="btn-back-evaluacion" style={{ background: '#3b82f6', color: 'white', border: 'none', padding: '0.5rem 1rem', borderRadius: 6, cursor: 'pointer', fontSize: '0.875rem', fontWeight: 500, display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          <ChevronLeft size={16} /> Volver a postulados
        </button>
      </div>

      <div style={{ background: '#f8fafc', padding: '1.25rem', borderRadius: 6, marginBottom: '1.5rem', border: '1px solid #e2e8f0' }}>
        <div style={{ fontSize: '1.5rem', fontWeight: 700, color: '#0f172a' }}>{company.name}</div>
        <div style={{ color: '#64748b', marginTop: 4 }}>Representante Legal: {company.representative}</div>
        {company.convocatoria && <div style={{ color: '#94a3b8', fontSize: '0.85rem', marginTop: 4 }}>Convocatoria: {company.convocatoria}</div>}
      </div>

      <h3 style={{ marginBottom: '1rem', color: '#0f172a', fontSize: '1.125rem' }}>Documentos de la Empresa</h3>
      <div style={{ display: 'grid', gap: '0.5rem', marginBottom: '1.5rem' }} data-testid="document-list">
        {documents.map((d) => {
          const isSubsan = d.status === 'subsanation';
          return (
            <div key={d.id} style={{ background: '#f8fafc', padding: '0.85rem 1rem', borderRadius: 6, border: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, borderLeft: isSubsan ? '4px solid #f59e0b' : '1px solid #e2e8f0' }}>
              <div>
                <div style={{ fontWeight: 500, color: '#0f172a' }}>{d.name}</div>
                {d.observation && <div style={{ fontSize: '0.8rem', color: '#64748b', marginTop: 2 }}>{d.observation}</div>}
              </div>
              <span style={{
                background: d.status === 'approved' ? '#dcfce7' : d.status === 'subsanation' ? '#fef3c7' : '#f1f5f9',
                color: d.status === 'approved' ? '#166534' : d.status === 'subsanation' ? '#92400e' : '#475569',
                padding: '0.25rem 0.75rem', borderRadius: 12, fontSize: '0.75rem', fontWeight: 600
              }}>{statusText(d.status)}</span>
            </div>
          );
        })}
      </div>

      <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
        <button onClick={openPreview} data-testid="btn-previsualizar" style={btnStyle('#6366f1')}>
          <Eye size={16} /> Previsualizar Documentos
        </button>
        <button onClick={openReport} data-testid="btn-informe" style={btnStyle('#10b981')}>
          <FileText size={16} /> Generar Informe de Calificación
        </button>
        <button onClick={openNotify} data-testid="btn-notificar" style={btnStyle('#8b5cf6')}>
          <Mail size={16} /> Notificar a la Empresa
        </button>
      </div>
    </Card>
  );
};

const btnStyle = (bg) => ({
  background: bg, color: 'white', border: 'none', padding: '0.75rem 1.5rem', borderRadius: 6, cursor: 'pointer',
  fontSize: '0.95rem', fontWeight: 500, display: 'inline-flex', alignItems: 'center', gap: 8
});

// ---------- Subsanaciones ----------
const SubsanacionesView = ({ subsanaciones, onBack, onRevisar }) => {
  const pendientes = subsanaciones.filter(s => s.status === 'pending').length;
  const aprobadas = subsanaciones.filter(s => s.status === 'approved').length;
  const rechazadas = subsanaciones.filter(s => s.status === 'rejected').length;
  return (
    <Card>
      <SectionHeader title="Subsanaciones" onBack={onBack} testId="btn-back-subsanaciones" />
      <StatsGrid stats={[
        { label: 'Pendientes de Revisión', value: pendientes },
        { label: 'Subsanadas', value: aprobadas },
        { label: 'Rechazadas', value: rechazadas },
      ]} />
      <div style={{ display: 'grid', gap: '0.75rem' }} data-testid="subsanaciones-list">
        {subsanaciones.map((s) => (
          <div key={s.id} style={{ background: '#f8fafc', padding: '1rem 1.25rem', borderRadius: 6, border: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
            <div style={{ flex: 1, minWidth: 250 }}>
              <div style={{ fontWeight: 600, color: '#0f172a' }}>{s.companyName}</div>
              <div style={{ fontSize: '0.85rem', color: '#64748b', marginTop: 4 }}>
                <strong>Documento:</strong> {s.documentName} · <strong>Obs:</strong> {s.oldObservation}
              </div>
            </div>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              {statusBadge(s.status === 'approved' ? 'completed' : s.status === 'rejected' ? 'rejected' : 'subsan')}
              {s.status === 'pending' && (
                <button onClick={() => onRevisar(s)} data-testid={`btn-revisar-${s.id}`} style={{ background: '#f59e0b', color: 'white', border: 'none', padding: '0.5rem 1rem', borderRadius: 6, cursor: 'pointer', fontSize: '0.85rem', fontWeight: 500 }}>Revisar</button>
              )}
            </div>
          </div>
        ))}
      </div>
    </Card>
  );
};

// ---------- Logout ----------
const LogoutView = ({ onCancel, onConfirm }) => (
  <Card style={{ textAlign: 'center', padding: '3rem 2rem' }}>
    <div style={{ display: 'inline-flex', padding: 20, background: '#fee2e2', borderRadius: '50%', marginBottom: '1rem' }}>
      <LogOut size={40} color="#dc2626" />
    </div>
    <h2 style={{ fontSize: '1.5rem', fontWeight: 700, color: '#0f172a', marginBottom: '0.5rem' }}>¿Desea cerrar sesión?</h2>
    <p style={{ color: '#64748b', marginBottom: '2rem' }}>Al cerrar sesión deberá ingresar sus credenciales nuevamente para acceder al sistema.</p>
    <div style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap' }}>
      <button onClick={onCancel} data-testid="btn-cancel-logout" style={{ background: '#64748b', color: 'white', border: 'none', padding: '0.75rem 2rem', borderRadius: 6, cursor: 'pointer', fontSize: '1rem', fontWeight: 600 }}>Cancelar</button>
      <button onClick={onConfirm} data-testid="btn-confirm-logout" style={{ background: '#dc2626', color: 'white', border: 'none', padding: '0.75rem 2rem', borderRadius: 6, cursor: 'pointer', fontSize: '1rem', fontWeight: 600 }}>Cerrar Sesión</button>
    </div>
  </Card>
);

// ============ Modals ============
const ModalWrap = ({ children, onClose, title, testId }) => (
  <div
    onClick={onClose}
    data-testid={testId}
    style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}
  >
    <div onClick={(e) => e.stopPropagation()} style={{ background: 'white', padding: '2rem', borderRadius: 8, maxWidth: 900, width: '100%', maxHeight: '85vh', overflowY: 'auto' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
        <h2 style={{ margin: 0, fontSize: '1.25rem', fontWeight: 700, color: '#0f172a' }}>{title}</h2>
        <button onClick={onClose} style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: '#64748b' }}><X size={22} /></button>
      </div>
      {children}
    </div>
  </div>
);

const PreviewModal = ({ company, documents, onClose, onSave, onPreviewFile }) => {
  const [docs, setDocs] = useState(documents);
  const update = (id, patch) => setDocs(prev => prev.map(d => d.id === id ? { ...d, ...patch } : d));
  return (
    <ModalWrap onClose={onClose} title={`Revisión de documentos — ${company.name}`} testId="modal-preview">
      <div style={{ display: 'grid', gap: '1rem' }}>
        {docs.map((d) => (
          <div key={d.id} style={{ background: '#f8fafc', padding: '1rem', borderRadius: 6, border: '1px solid #e2e8f0' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8, gap: 8, flexWrap: 'wrap' }}>
              <div style={{ fontWeight: 600, color: '#0f172a' }}>{d.name}</div>
              {d.url && (
                <button
                  type="button"
                  onClick={() => onPreviewFile({ name: d.name, url: d.url })}
                  data-testid={`btn-preview-file-${d.id}`}
                  style={{
                    background: '#3b82f6', color: 'white', border: 'none', padding: '0.4rem 0.85rem',
                    borderRadius: 6, cursor: 'pointer', fontSize: '0.8rem', fontWeight: 500,
                    display: 'inline-flex', alignItems: 'center', gap: 6,
                  }}
                >
                  <Eye size={14} /> Ver archivo
                </button>
              )}
            </div>
            <div style={{ display: 'flex', gap: 12, marginBottom: 8, flexWrap: 'wrap' }}>
              {['approved', 'subsanation'].map((opt) => (
                <label key={opt} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '0.5rem 0.85rem', border: `2px solid ${d.status === opt ? (opt === 'approved' ? '#10b981' : '#f59e0b') : '#e2e8f0'}`, borderRadius: 6, cursor: 'pointer', background: d.status === opt ? (opt === 'approved' ? '#dcfce7' : '#fef3c7') : 'white' }}>
                  <input type="radio" name={`doc-${d.id}`} checked={d.status === opt} onChange={() => update(d.id, { status: opt })} />
                  <span style={{ fontSize: '0.85rem', fontWeight: 500 }}>{opt === 'approved' ? 'Aprobar' : 'Solicitar subsanación'}</span>
                </label>
              ))}
            </div>
            <textarea
              value={d.observation}
              onChange={(e) => update(d.id, { observation: e.target.value })}
              placeholder="Observaciones (opcional)"
              style={{ width: '100%', padding: '0.65rem', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: '0.9rem', fontFamily: 'inherit', minHeight: 60, resize: 'vertical' }}
            />
          </div>
        ))}
      </div>
      <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end', marginTop: '1.5rem' }}>
        <button onClick={onClose} style={{ background: '#64748b', color: 'white', border: 'none', padding: '0.65rem 1.25rem', borderRadius: 6, cursor: 'pointer', fontWeight: 500 }}>Cancelar</button>
        <button onClick={() => onSave(docs)} data-testid="btn-save-review" style={{ background: '#10b981', color: 'white', border: 'none', padding: '0.65rem 1.25rem', borderRadius: 6, cursor: 'pointer', fontWeight: 500 }}>Guardar Revisión</button>
      </div>
    </ModalWrap>
  );
};

const FilePreviewModal = ({ file, onClose }) => {
  const ext = getFileExt(file.url);
  const fullUrl = `${process.env.REACT_APP_BACKEND_URL}${file.url}`;
  const isPdf = ext === 'pdf';
  const isImage = ['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext);
  const isExcel = ['xls', 'xlsx'].includes(ext);
  return (
    <div
      onClick={onClose}
      data-testid="file-preview-modal"
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)', zIndex: 1100, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}
    >
      <div onClick={(e) => e.stopPropagation()} style={{ background: 'white', borderRadius: 8, width: '95%', maxWidth: 1100, height: '90vh', display: 'flex', flexDirection: 'column' }}>
        <div style={{ padding: '0.85rem 1.25rem', borderBottom: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <FileText size={18} color="#3b82f6" />
            <div>
              <div style={{ fontWeight: 600, color: '#0f172a' }}>{file.name}</div>
              <div style={{ fontSize: '0.75rem', color: '#64748b', textTransform: 'uppercase' }}>{ext}</div>
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <a href={fullUrl} target="_blank" rel="noreferrer" download data-testid="btn-download-preview"
              style={{ background: '#10b981', color: 'white', textDecoration: 'none', padding: '0.5rem 0.9rem', borderRadius: 6, fontSize: '0.85rem', fontWeight: 500, display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              <Download size={14} /> Descargar
            </a>
            <button onClick={onClose} data-testid="btn-close-file-preview" style={{ background: '#f1f5f9', border: 'none', padding: '0.5rem', borderRadius: 6, cursor: 'pointer' }}>
              <X size={16} />
            </button>
          </div>
        </div>
        <div style={{ flex: 1, overflow: 'hidden', background: '#f8fafc' }}>
          {isPdf && (
            <iframe title={file.name} src={fullUrl} style={{ width: '100%', height: '100%', border: 'none' }} data-testid="preview-iframe" />
          )}
          {isImage && (
            <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
              <img src={fullUrl} alt={file.name} style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain', borderRadius: 6 }} />
            </div>
          )}
          {isExcel && (
            <div style={{ height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 12, padding: 40, textAlign: 'center' }}>
              <div style={{ width: 72, height: 72, borderRadius: '50%', background: '#dcfce7', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <FileText size={38} color="#166534" />
              </div>
              <div style={{ fontSize: '1.1rem', fontWeight: 600, color: '#0f172a' }}>Archivo Excel</div>
              <div style={{ color: '#64748b', maxWidth: 380 }}>Los archivos Excel no se pueden previsualizar en el navegador. Descárgalo para abrirlo con Microsoft Excel, Google Sheets o LibreOffice.</div>
              <a href={fullUrl} target="_blank" rel="noreferrer" download
                style={{ background: '#10b981', color: 'white', textDecoration: 'none', padding: '0.6rem 1.2rem', borderRadius: 6, marginTop: 8, fontWeight: 500 }}>
                Descargar {ext.toUpperCase()}
              </a>
            </div>
          )}
          {!isPdf && !isImage && !isExcel && (
            <div style={{ padding: 40, textAlign: 'center', color: '#64748b' }}>
              Formato no soportado para previsualización. <a href={fullUrl} target="_blank" rel="noreferrer" style={{ color: '#3b82f6' }}>Descargar archivo</a>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const ReportModal = ({ postulaciones, onClose, onGenerate }) => {
  const [sel, setSel] = useState('');
  const [obs, setObs] = useState('');
  return (
    <ModalWrap onClose={onClose} title="Generar Informe de Calificación" testId="modal-report">
      <label style={{ display: 'block', marginBottom: 8, color: '#475569', fontWeight: 500, fontSize: '0.9rem' }}>Empresa</label>
      <select value={sel} onChange={(e) => setSel(e.target.value)} style={{ width: '100%', padding: '0.65rem', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: '0.95rem', marginBottom: '1rem' }}>
        <option value="">Seleccionar empresa…</option>
        {postulaciones.map(p => <option key={p.id} value={p.id}>{p.user_name || p.company_name}</option>)}
      </select>
      <label style={{ display: 'block', marginBottom: 8, color: '#475569', fontWeight: 500, fontSize: '0.9rem' }}>Observaciones generales</label>
      <textarea value={obs} onChange={(e) => setObs(e.target.value)} rows={5} style={{ width: '100%', padding: '0.75rem', border: '1px solid #e2e8f0', borderRadius: 6, fontFamily: 'inherit', fontSize: '0.95rem' }} />
      <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end', marginTop: '1.5rem' }}>
        <button onClick={onClose} style={{ background: '#64748b', color: 'white', border: 'none', padding: '0.65rem 1.25rem', borderRadius: 6, cursor: 'pointer', fontWeight: 500 }}>Cancelar</button>
        <button onClick={onGenerate} disabled={!sel} data-testid="btn-generate-report" style={{ background: sel ? '#10b981' : '#94a3b8', color: 'white', border: 'none', padding: '0.65rem 1.25rem', borderRadius: 6, cursor: sel ? 'pointer' : 'not-allowed', fontWeight: 500 }}>Generar Informe</button>
      </div>
    </ModalWrap>
  );
};

const NotifyModal = ({ postulaciones, onClose, onSend }) => {
  const [sel, setSel] = useState('');
  const [msg, setMsg] = useState('');
  return (
    <ModalWrap onClose={onClose} title="Notificar a la Empresa" testId="modal-notify">
      <label style={{ display: 'block', marginBottom: 8, color: '#475569', fontWeight: 500, fontSize: '0.9rem' }}>Empresa</label>
      <select value={sel} onChange={(e) => setSel(e.target.value)} style={{ width: '100%', padding: '0.65rem', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: '0.95rem', marginBottom: '1rem' }}>
        <option value="">Seleccionar empresa…</option>
        {postulaciones.map(p => <option key={p.id} value={p.id}>{p.user_name || p.company_name}</option>)}
      </select>
      <label style={{ display: 'block', marginBottom: 8, color: '#475569', fontWeight: 500, fontSize: '0.9rem' }}>Mensaje</label>
      <textarea value={msg} onChange={(e) => setMsg(e.target.value)} rows={5} style={{ width: '100%', padding: '0.75rem', border: '1px solid #e2e8f0', borderRadius: 6, fontFamily: 'inherit', fontSize: '0.95rem' }} />
      <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end', marginTop: '1.5rem' }}>
        <button onClick={onClose} style={{ background: '#64748b', color: 'white', border: 'none', padding: '0.65rem 1.25rem', borderRadius: 6, cursor: 'pointer', fontWeight: 500 }}>Cancelar</button>
        <button onClick={onSend} disabled={!sel || !msg} data-testid="btn-send-notify" style={{ background: (sel && msg) ? '#8b5cf6' : '#94a3b8', color: 'white', border: 'none', padding: '0.65rem 1.25rem', borderRadius: 6, cursor: (sel && msg) ? 'pointer' : 'not-allowed', fontWeight: 500 }}>Enviar Notificación</button>
      </div>
    </ModalWrap>
  );
};

const SubsanationReviewModal = ({ subsanation, onClose, onDecide, onPreviewFile }) => {
  const [decision, setDecision] = useState('');
  return (
    <ModalWrap onClose={onClose} title={`Revisar subsanación — ${subsanation.companyName}`} testId="modal-subsanacion">
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1.5rem' }}>
        <div style={{ background: '#fef2f2', padding: '1rem', borderRadius: 6, borderLeft: '4px solid #ef4444' }}>
          <div style={{ fontSize: '0.75rem', color: '#991b1b', fontWeight: 600, marginBottom: 4, textTransform: 'uppercase' }}>Anterior</div>
          <div style={{ fontWeight: 600, color: '#0f172a' }}>{subsanation.documentName}</div>
          <div style={{ fontSize: '0.85rem', color: '#64748b', marginTop: 6 }}>{subsanation.oldObservation}</div>
          <div style={{ fontSize: '0.8rem', color: '#94a3b8', marginTop: 4 }}>Fecha: {subsanation.oldDate}</div>
          {subsanation.oldUrl && (
            <button
              type="button"
              onClick={() => onPreviewFile && onPreviewFile({ name: `${subsanation.documentName} (anterior)`, url: subsanation.oldUrl })}
              style={{ marginTop: 8, background: '#ef4444', color: 'white', border: 'none', padding: '0.35rem 0.75rem', borderRadius: 6, cursor: 'pointer', fontSize: '0.75rem', fontWeight: 500, display: 'inline-flex', alignItems: 'center', gap: 4 }}
            >
              <Eye size={12} /> Ver original
            </button>
          )}
        </div>
        <div style={{ background: '#f0fdf4', padding: '1rem', borderRadius: 6, borderLeft: '4px solid #10b981' }}>
          <div style={{ fontSize: '0.75rem', color: '#065f46', fontWeight: 600, marginBottom: 4, textTransform: 'uppercase' }}>Nueva versión</div>
          <div style={{ fontWeight: 600, color: '#0f172a' }}>{subsanation.documentName}</div>
          <div style={{ fontSize: '0.85rem', color: '#64748b', marginTop: 6 }}>Documento corregido por {subsanation.representative}</div>
          <div style={{ fontSize: '0.8rem', color: '#94a3b8', marginTop: 4 }}>Fecha: {subsanation.newDate}</div>
          {subsanation.newUrl && (
            <button
              type="button"
              onClick={() => onPreviewFile && onPreviewFile({ name: `${subsanation.documentName} (nueva versión)`, url: subsanation.newUrl })}
              data-testid="btn-preview-new-version"
              style={{ marginTop: 8, background: '#10b981', color: 'white', border: 'none', padding: '0.35rem 0.75rem', borderRadius: 6, cursor: 'pointer', fontSize: '0.75rem', fontWeight: 500, display: 'inline-flex', alignItems: 'center', gap: 4 }}
            >
              <Eye size={12} /> Ver nueva versión
            </button>
          )}
        </div>
      </div>

      <label style={{ display: 'block', marginBottom: 8, color: '#475569', fontWeight: 500, fontSize: '0.9rem' }}>Decisión</label>
      <div style={{ display: 'flex', gap: 12, marginBottom: '1rem', flexWrap: 'wrap' }}>
        {[
          { v: 'approved', label: 'Aprobar', color: '#10b981', bg: '#dcfce7' },
          { v: 'rejected', label: 'Rechazar', color: '#ef4444', bg: '#fee2e2' },
        ].map((opt) => (
          <label key={opt.v} style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 8, padding: '0.75rem 1rem', border: `2px solid ${decision === opt.v ? opt.color : '#e2e8f0'}`, borderRadius: 6, cursor: 'pointer', background: decision === opt.v ? opt.bg : 'white', minWidth: 140 }}>
            <input type="radio" name="decision" checked={decision === opt.v} onChange={() => setDecision(opt.v)} />
            <span style={{ fontWeight: 500 }}>{opt.label}</span>
          </label>
        ))}
      </div>

      <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
        <button onClick={onClose} style={{ background: '#64748b', color: 'white', border: 'none', padding: '0.65rem 1.25rem', borderRadius: 6, cursor: 'pointer', fontWeight: 500 }}>Cancelar</button>
        <button onClick={() => onDecide(decision)} disabled={!decision} data-testid="btn-update-subsanacion" style={{ background: decision ? '#10b981' : '#94a3b8', color: 'white', border: 'none', padding: '0.65rem 1.25rem', borderRadius: 6, cursor: decision ? 'pointer' : 'not-allowed', fontWeight: 500 }}>Actualizar</button>
      </div>
    </ModalWrap>
  );
};
